import { Badge, Group, Paper, ScrollArea, Table, Text } from '@mantine/core';
import { IconActivity } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { SessionStatusBadge } from '@/components/domain/SessionStatusBadge';
import { PageHeader } from '@/components/ui/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/StateViews';
import { ADMIN_SESSIONS_KEY, fetchAdminSessions } from './api';

const date = (value: string) => new Date(value).toLocaleString('ru-RU', {
  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
});

export function AdminSessionsPage() {
  const query = useQuery({ queryKey: ADMIN_SESSIONS_KEY, queryFn: fetchAdminSessions });
  const items = query.data?.items ?? [];
  return (
    <>
      <PageHeader
        eyebrow="Администрирование"
        title="История сессий"
        description="Ожидающие, идущие, приостановленные и только что завершённые сессии. По документам приостановленная сессия удаляется через 45 дней, завершённая — через час после завершения."
      />
      {query.isLoading ? <LoadingState label="Загружаем сессии..." /> : query.isError ? (
        <ErrorState onRetry={() => void query.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState icon={<IconActivity size={28} />} title="Сессий пока нет" />
      ) : (
        <Paper withBorder>
          <ScrollArea>
            <Table miw={820} verticalSpacing="sm">
              <Table.Thead><Table.Tr>
                <Table.Th>Сессия</Table.Th><Table.Th>Ведущий</Table.Th><Table.Th>Статус</Table.Th>
                {/*
                  Колонки «Версия» здесь не было и не должно быть.
                  `stateVersion` — счётчик изменений, по которому клиенты
                  понимают, не разъехались ли их снимки партии. Администратору
                  он ничего не говорит и читается как версия игры: «Твоё солнце
                  таланта, версия 47». Само поле остаётся техническим и живёт
                  в модели и API как жило.
                */}
                <Table.Th>Игроки</Table.Th><Table.Th>Создана</Table.Th>
              </Table.Tr></Table.Thead>
              <Table.Tbody>{items.map((item) => (
                <Table.Tr key={item.id}>
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                      <Badge size="xs" color={item.kind === 'game' ? 'gold' : 'teal'}>
                        {item.kind === 'game' ? 'Игра' : 'МАК-сессия'}
                      </Badge>
                      <Text fw={600} size="sm">{item.title}</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td><Text size="sm">{item.ownerDisplayName}</Text><Text size="xs" c="dimmed">{item.ownerEmail}</Text></Table.Td>
                  <Table.Td><SessionStatusBadge status={item.status} /></Table.Td>
                  <Table.Td>
                    {/* Игровые места; служебная запись ведущего сюда не входит. */}
                    <Text size="sm">{item.participantCount}</Text>
                    {item.demoScenario ? (
                      <Text size="xs" c="dimmed">
                        Демо · {item.demoScenario === 'solo' ? 'для себя' : 'как ведущий'}
                      </Text>
                    ) : null}
                    {item.hostPlays && item.kind === 'game' && item.demoScenario !== 'solo' ? (
                      <Text size="xs" c="dimmed">в т. ч. ведущий</Text>
                    ) : null}
                  </Table.Td>
                  <Table.Td><Text size="sm" c="dimmed">{date(item.createdAt)}</Text></Table.Td>
                </Table.Tr>
              ))}</Table.Tbody>
            </Table>
          </ScrollArea>
        </Paper>
      )}
    </>
  );
}
